<script setup>
import { reactive, ref, watch } from "vue";
import { getRequest, postRequest } from "@/utils/request.js";

const title = ref("");
const open = ref(false);
const confirmLoading = ref(false);
const formModel = ref({});
const editable = ref(false);
const photoList = ref([]);
const auditType = ref("");
const formRef = ref();
const emit = defineEmits(["ok"]);

const rules = reactive({
  completeContentInput: [
    {
      required: false,
      message: "请输入处理结果",
      trigger: "blur",
    },
  ],
  rejectReasonInput: [
    {
      required: false,
      message: "请输入驳回原因",
      trigger: "blur",
    },
  ],
  returnVisitContentInput: [
    {
      required: false,
      message: "请输入回访记录",
      trigger: "blur",
    },
  ],
});

watch(
  () => auditType.value,
  (auditType) => {
    if (auditType === "complete") {
      rules.completeContentInput[0].required = true;
      rules.rejectReasonInput[0].required = false;
      rules.returnVisitContentInput[0].required = false;
    } else if (auditType === "reject") {
      rules.completeContentInput[0].required = false;
      rules.rejectReasonInput[0].required = true;
      rules.returnVisitContentInput[0].required = false;
    } else if (auditType === "returnVisit") {
      rules.completeContentInput[0].required = false;
      rules.rejectReasonInput[0].required = false;
      rules.returnVisitContentInput[0].required = true;
    }
  },
);

const handleShow = (record) => {
  formModel.value = Object.assign({}, { ...record });
  open.value = true;
  editable.value = false;
  loadData();
};

const handleComplete = (record) => {
  formModel.value = Object.assign({}, { ...record });
  open.value = true;
  auditType.value = "complete";
  loadData();
};

const handleReject = (record) => {
  formModel.value = Object.assign({}, { ...record });
  open.value = true;
  auditType.value = "reject";
  loadData();
};

const handleReturnVisit = (record) => {
  formModel.value = Object.assign({}, { ...record });
  open.value = true;
  auditType.value = "returnVisit";
  loadData();
};

const loadData = () => {
  getRequest(`/complaint/${formModel.value.id}`).then((res) => {
    if (res.success) {
      formModel.value = Object.assign({}, { ...res.data });
      if (res.data.photo) {
        photoList.value = res.data.photo.split(",");
      }
    }
  });
};

const handleConfirm = () => {
  formRef.value.validate().then(async () => {
    let url = "";
    let requestBody = {};
    if (auditType.value === "complete") {
      url = "/complaint/complete";
      requestBody = {
        ...formModel.value,
        completeContent: formModel.value.completeContentInput,
      };
    }
    if (auditType.value === "reject") {
      url = "/complaint/reject";
      requestBody = {
        ...formModel.value,
        rejectReason: formModel.value.rejectReasonInput,
      };
    }
    if (auditType.value === "returnVisit") {
      url = "/complaint/returnVisit";
      requestBody = {
        ...formModel.value,
        returnVisitContent: formModel.value.returnVisitContentInput,
      };
    }
    postRequest(url, requestBody).then((response) => {
      if (response.success) {
        emit("ok");
        handleClose();
      }
    });
  });
};

const handleClose = () => {
  open.value = false;
  formModel.value = Object.assign({});
};

defineExpose({
  handleShow,
  handleComplete,
  handleReject,
  handleReturnVisit,
  title,
});
</script>

<template>
  <a-modal
    width="1200px"
    :open="open"
    :title="title"
    :confirm-loading="confirmLoading"
    @ok="handleConfirm"
    @cancel="handleClose"
  >
    <a-descriptions
      style="padding-top: 10px"
      title="举报信息"
      bordered
      size="small"
      :labelStyle="{
        width: 'fit-content',
        wordBreak: 'keep-all',
      }"
    >
      <a-descriptions-item label="举报人">
        <template v-if="formModel['userType'] === 'JOB_SEEKER'">
          <a-tag color="blue"> 求职者</a-tag>
        </template>
        <template v-if="formModel['userType'] === 'RECRUITER'">
          <a-tag color="green"> 招聘者</a-tag>
        </template>
        {{ formModel.realName }}
      </a-descriptions-item>
      <a-descriptions-item label="举报原因">
        {{ formModel.typeName }}
      </a-descriptions-item>
      <a-descriptions-item label="状态">
        <template v-if="formModel.status === 'APPLIED'"> 已申请</template>
        <template v-if="formModel.status === 'ACCEPTED'"> 已受理</template>
        <template v-if="formModel.status === 'REJECT'"> 驳回</template>
        <template v-if="formModel.status === 'COMPLETE'"> 已处理</template>
        <template v-if="formModel.status === 'RETURN_VISIT'"> 已回访</template>
      </a-descriptions-item>
      <a-descriptions-item label="举报时间">
        {{ formModel.appliedTime }}
      </a-descriptions-item>
      <a-descriptions-item label="举报内容" :span="2">
        {{ formModel.content }}
      </a-descriptions-item>
      <a-descriptions-item label="受理时间" :span="3">
        {{ formModel.acceptedTime }}
      </a-descriptions-item>
      <a-descriptions-item label="驳回时间">
        {{ formModel.rejectTime }}
      </a-descriptions-item>
      <a-descriptions-item label="驳回原因" :span="2">
        {{ formModel.rejectReason }}
      </a-descriptions-item>
      <a-descriptions-item label="处理时间">
        {{ formModel.completeTime }}
      </a-descriptions-item>
      <a-descriptions-item label="处理结果" :span="2">
        {{ formModel.completeContent }}
      </a-descriptions-item>
      <a-descriptions-item label="回访时间">
        {{ formModel.returnVisitTime }}
      </a-descriptions-item>
      <a-descriptions-item label="回访记录" :span="2">
        {{ formModel.returnVisitContent }}
      </a-descriptions-item>
    </a-descriptions>
    <a-descriptions
      v-if="photoList?.length"
      style="padding-top: 30px"
      title="凭证"
      size="small"
    >
    </a-descriptions>
    <div v-if="photoList?.length">
      <a-image-preview-group>
        <a-image
          v-for="photo in photoList"
          :key="photo.id"
          :width="200"
          :src="$imageUrl(photo)"
        />
      </a-image-preview-group>
    </div>
    <a-form
      style="margin-top: 20px"
      :model="formModel"
      :rules="rules"
      ref="formRef"
    >
      <a-form-item
        label="处理结果"
        name="completeContentInput"
        v-if="auditType === 'complete'"
      >
        <a-textarea v-model:value="formModel.completeContentInput"></a-textarea>
      </a-form-item>
      <a-form-item
        label="回访记录"
        name="returnVisitContentInput"
        v-if="auditType === 'returnVisit'"
      >
        <a-textarea
          v-model:value="formModel.returnVisitContentInput"
        ></a-textarea>
      </a-form-item>
      <a-form-item
        label="回访记录"
        name="rejectReasonInput"
        v-if="auditType === 'reject'"
      >
        <a-textarea v-model:value="formModel.rejectReasonInput"></a-textarea>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<style scoped lang="scss"></style>
